import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  WorkflowAutoCreationDayFilter,
  WorkflowAutoCreationLeadUnit,
  type WorkflowAutoCreationRule,
  WorkflowAutoCreationRuleNotice,
  WorkflowPlanAutomation,
} from '@ebalance/proto/bffuser/v1/workflow_auto_creation_rule_model_pb'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import {
  type TimeOfDay,
  TimeOfDaySchema,
} from '@ebalance/proto/google/type/timeofday_pb'

import { canonicalWorkflowSection } from '~/models/workflowSection'

const K = 'page:adminWorkflowTemplates.autoCreationRule'

/** 一覧・モーダルの雛形の名前と計画断面を引くための最小の雛形情報。 */
export type AutoCreationRuleTemplate = {
  id: string
  name: string
  section: WorkflowSection
  /**
   * 計画を提出するサブステップに要承認があり、計画の自動化は雛形どおりだけ (AC-WFT-043)。
   * backend の判定 (List の locked template ids) をそのまま持つ。
   */
  planAutomationLocked: boolean
}

export type AutoCreationRuleSchedule = {
  leadValue: number
  leadUnit: WorkflowAutoCreationLeadUnit
  /** JST の `HH:mm`。 */
  fireTimeHm: string
  dayFilter: WorkflowAutoCreationDayFilter
  planAutomation: WorkflowPlanAutomation
}

export type AutoCreationRuleRow = {
  id: string
  template: AutoCreationRuleTemplate
  areaCode: AreaCode
  enabled: boolean
  schedule: AutoCreationRuleSchedule
  // 楽観ロックの基準なので受け取った Timestamp のまま持つ
  updatedAt: Timestamp | undefined
  /** 起票していない理由 (AC-WFT-050 / AC-WFT-057)。backend が決めた値をそのまま持つ。 */
  notice: WorkflowAutoCreationRuleNotice
  /** 保存値が雛形の要承認で使われない (AC-WFT-047)。backend の判定をそのまま持つ。 */
  planAutomationUnused: boolean
}

const NOTICE_LABEL_KEYS: Partial<
  Record<WorkflowAutoCreationRuleNotice, string>
> = {
  [WorkflowAutoCreationRuleNotice.BG_MISSING]: `${K}.notice.bgMissing`,
  [WorkflowAutoCreationRuleNotice.BG_DUPLICATED]: `${K}.notice.bgDuplicated`,
  [WorkflowAutoCreationRuleNotice.MANUAL_OPEN]: `${K}.notice.manualOpen`,
}

/**
 * 状態の列に添える、起票していない理由の文 (AC-WFT-050 / AC-WFT-057)。知らせが無い (UNSPECIFIED)・
 * 知らない値の時は undefined で、文を出さない。
 */
export function autoCreationRuleNoticeLabelKey(
  notice: WorkflowAutoCreationRuleNotice,
): string | undefined {
  return NOTICE_LABEL_KEYS[notice]
}

/**
 * 定期のルールにできる雛形か (spec §4 定期「対象日の並びは 単日 = 毎日 / 週間 = 毎週土曜」)。
 * 月間・年間・未指定は backend も FAILED_PRECONDITION で拒否する。
 */
export function isScheduleEligibleSection(section: WorkflowSection): boolean {
  const canonical = canonicalWorkflowSection(section)
  return (
    canonical === WorkflowSection.SINGLE_DAY ||
    canonical === WorkflowSection.WEEKLY
  )
}

/** 計画断面から導く対象日の並び (AC-WFT-020)。定期にできない断面は undefined。 */
export function targetDaysLabelKey(
  section: WorkflowSection,
): string | undefined {
  switch (canonicalWorkflowSection(section)) {
    case WorkflowSection.SINGLE_DAY:
      return `${K}.targetDays.everyDay`
    case WorkflowSection.WEEKLY:
      return `${K}.targetDays.everySaturday`
    default:
      return undefined
  }
}

type SelectableLeadUnit = Exclude<
  WorkflowAutoCreationLeadUnit,
  WorkflowAutoCreationLeadUnit.UNSPECIFIED
>
type SelectableDayFilter = Exclude<
  WorkflowAutoCreationDayFilter,
  WorkflowAutoCreationDayFilter.UNSPECIFIED
>

/**
 * 新規ルールで雛形を選んだ時の起票リードの既定 (AC-WFT-021: 単日 = 1 日 / 週間 = 5 日)。
 * 定期にできない断面は雛形の選択肢に出ないので、単日と同じ値に落とす。
 */
export function defaultLeadForSection(section: WorkflowSection): {
  leadValue: number
  leadUnit: WorkflowAutoCreationLeadUnit.DAY
} {
  return {
    leadValue:
      canonicalWorkflowSection(section) === WorkflowSection.WEEKLY ? 5 : 1,
    leadUnit: WorkflowAutoCreationLeadUnit.DAY,
  }
}

export const LEAD_UNIT_OPTIONS: readonly SelectableLeadUnit[] = [
  WorkflowAutoCreationLeadUnit.DAY,
  WorkflowAutoCreationLeadUnit.MONTH,
  WorkflowAutoCreationLeadUnit.YEAR,
]

export const LEAD_UNIT_LABEL_KEYS: Record<SelectableLeadUnit, string> = {
  [WorkflowAutoCreationLeadUnit.DAY]: `${K}.leadUnit.day`,
  [WorkflowAutoCreationLeadUnit.MONTH]: `${K}.leadUnit.month`,
  [WorkflowAutoCreationLeadUnit.YEAR]: `${K}.leadUnit.year`,
}

export const DAY_FILTER_OPTIONS: readonly SelectableDayFilter[] = [
  WorkflowAutoCreationDayFilter.EVERY_DAY,
  WorkflowAutoCreationDayFilter.WEEKDAYS,
  WorkflowAutoCreationDayFilter.WEEKDAYS_EXCLUDING_HOLIDAYS,
]

export const DAY_FILTER_LABEL_KEYS: Record<SelectableDayFilter, string> = {
  [WorkflowAutoCreationDayFilter.EVERY_DAY]: `${K}.dayFilter.everyDay`,
  [WorkflowAutoCreationDayFilter.WEEKDAYS]: `${K}.dayFilter.weekdays`,
  [WorkflowAutoCreationDayFilter.WEEKDAYS_EXCLUDING_HOLIDAYS]: `${K}.dayFilter.weekdaysExcludingHolidays`,
}

export function dayFilterLabelKey(
  dayFilter: WorkflowAutoCreationDayFilter,
): string | undefined {
  return dayFilter === WorkflowAutoCreationDayFilter.UNSPECIFIED
    ? undefined
    : DAY_FILTER_LABEL_KEYS[dayFilter]
}

/** i18n key と差し込む値。 */
export type LabelSpec = { key: string; count?: number }

/**
 * 起票タイミング列の起票リードの表示 (AC-WFT-020「対象日の1日前」)。
 * リード 0 は対象日の当日に起票するので数値を出さない。
 */
export function leadLabelSpec(
  leadValue: number,
  leadUnit: WorkflowAutoCreationLeadUnit,
): LabelSpec | undefined {
  if (leadValue === 0) return { key: `${K}.lead.sameDay` }
  switch (leadUnit) {
    case WorkflowAutoCreationLeadUnit.DAY:
      return { key: `${K}.lead.daysBefore`, count: leadValue }
    case WorkflowAutoCreationLeadUnit.MONTH:
      return { key: `${K}.lead.monthsBefore`, count: leadValue }
    case WorkflowAutoCreationLeadUnit.YEAR:
      return { key: `${K}.lead.yearsBefore`, count: leadValue }
    default:
      return undefined
  }
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** `google.type.TimeOfDay` → `HH:mm` (起票時刻は分まで)。 */
export function formatTimeOfDayHm(time: TimeOfDay | undefined): string {
  if (time === undefined) return ''
  return `${pad2(time.hours)}:${pad2(time.minutes)}`
}

/** `HH:mm` → `google.type.TimeOfDay`。形式が違えば undefined。 */
export function parseTimeOfDayHm(value: string): TimeOfDay | undefined {
  const m = /^(\d{2}):(\d{2})$/.exec(value)
  if (m === null) return undefined
  const hours = Number(m[1])
  const minutes = Number(m[2])
  if (hours > 23 || minutes > 59) return undefined
  return create(TimeOfDaySchema, { hours, minutes, seconds: 0, nanos: 0 })
}

/** 雛形に List の locked template ids を重ねる (AC-WFT-043)。 */
export function withPlanAutomationLock<
  T extends Omit<AutoCreationRuleTemplate, 'planAutomationLocked'>,
>(
  templates: readonly T[],
  lockedTemplateIds: readonly string[],
): Array<T & { planAutomationLocked: boolean }> {
  const locked = new Set(lockedTemplateIds)
  return templates.map((tpl) => ({
    ...tpl,
    planAutomationLocked: locked.has(tpl.id),
  }))
}

/**
 * List の応答を一覧の行にする。雛形の一覧に無いルール (削除された・取得できない雛形) と、
 * 定期でないルールは編集も表示もできないので出さない。
 */
export function buildAutoCreationRuleRows(
  rules: readonly WorkflowAutoCreationRule[],
  templates: readonly AutoCreationRuleTemplate[],
  planAutomationUnusedRuleIds: readonly string[] = [],
): AutoCreationRuleRow[] {
  const byId = new Map(templates.map((tpl) => [tpl.id, tpl]))
  const unused = new Set(planAutomationUnusedRuleIds)
  const rows: AutoCreationRuleRow[] = []
  for (const rule of rules) {
    const template = byId.get(rule.workflowTemplateId)
    if (template === undefined) continue
    if (rule.trigger.case !== 'schedule') continue
    const schedule = rule.trigger.value
    rows.push({
      id: rule.id,
      template,
      areaCode: rule.areaCode,
      enabled: rule.enabled,
      schedule: {
        leadValue: schedule.leadValue,
        leadUnit: schedule.leadUnit,
        fireTimeHm: formatTimeOfDayHm(schedule.fireTime),
        dayFilter: schedule.dayFilter,
        planAutomation: schedule.planAutomation,
      },
      updatedAt: rule.updatedAt,
      notice: rule.notice,
      planAutomationUnused: unused.has(rule.id),
    })
  }
  return rows
}
